<template>
    <v-form
        ref="accessRoleForm"
        v-model="valid"
        :disabled="props.disabled || isSubmitting"
        :loading="fetchingAppDetails || fetchingRoleDetails || isSubmitting"
    >
        <v-row class="mb-8">
            <v-col cols="12">
                <v-autocomplete
                    v-model="formValues.access_id"
                    variant="outlined"
                    label="Acesso / Aplicacao *"
                    :rules="formRules.access_id"
                    :messages="formMessages.access_id"
                    :error-messages="formErrors.access_id"
                    :clearable="!(props.readonlyInputs.access_id || props.readonly)"
                    :disabled="props.disabledInputs.access_id || props.disabled || isSubmitting || fetchingAppDetails || fetchingRoleDetails"
                    :readonly="props.readonlyInputs.access_id || props.readonly"
                    :menu-icon="props.readonlyInputs.access_id || props.readonly ? '' : undefined"
                    :items="appsList"
                    item-value="id"
                    item-title="name"
                    :loading="fetchingAppDetails || isSubmitting"
                />
            </v-col>
        </v-row>

        <v-row class="mb-8">
            <v-col cols="12" md="6">
                <v-text-field
                    v-model="formValues.name"
                    variant="outlined"
                    label="Codigo do role *"
                    :clearable="!(props.readonlyInputs.name || props.readonly)"
                    :menu-icon="props.readonlyInputs.name || props.readonly ? '' : undefined"
                    :rules="formRules.name"
                    :messages="formMessages.name"
                    :error-messages="formErrors.name"
                    hint="Nome de codigo do role. E recomendado que siga a estrutura app.modelo.responsabilidade."
                    persistent-hint
                    :disabled="props.disabledInputs.name || props.disabled || isSubmitting || (fetchingAppDetails && mode != 'create') || fetchingRoleDetails"
                    :readonly="props.readonlyInputs.name || props.readonly"
                    :loading="(fetchingAppDetails && mode != 'create') || fetchingRoleDetails || isSubmitting"
                />
            </v-col>

            <v-col cols="12" md="6">
                <v-text-field
                    v-model="formValues.display_name"
                    variant="outlined"
                    label="Nome do role *"
                    :clearable="!(props.readonlyInputs.display_name || props.readonly)"
                    :menu-icon="props.readonlyInputs.display_name || props.readonly ? '' : undefined"
                    :rules="formRules.display_name"
                    :messages="formMessages.display_name"
                    :error-messages="formErrors.display_name"
                    hint="Nome apresentado para representar o role."
                    persistent-hint
                    :disabled="props.disabledInputs.display_name || props.disabled || isSubmitting || (fetchingAppDetails && mode != 'create') || fetchingRoleDetails"
                    :readonly="props.readonlyInputs.display_name || props.readonly"
                    :loading="(fetchingAppDetails && mode != 'create') || fetchingRoleDetails || isSubmitting"
                />
            </v-col>
        </v-row>

        <v-row class="mb-8">
            <v-col cols="12">
                <v-textarea
                    v-model="formValues.description"
                    variant="outlined"
                    label="Descricao do Role"
                    :clearable="!(props.readonlyInputs.description || props.readonly)"
                    :menu-icon="props.readonlyInputs.description || props.readonly ? '' : undefined"
                    :rules="formRules.description"
                    :messages="formMessages.description"
                    :error-messages="formErrors.description"
                    hint="Breve descricao das capacidades do Role."
                    persistent-hint
                    :disabled="props.disabledInputs.description || props.disabled || isSubmitting || (fetchingAppDetails && mode != 'create') || fetchingRoleDetails"
                    :readonly="props.readonlyInputs.description || props.readonly"
                    :loading="(fetchingAppDetails && mode != 'create') || fetchingRoleDetails || isSubmitting"
                />
            </v-col>
        </v-row>

        <v-divider />

        <v-row class="mb-8">
            <v-col cols="12">
                <v-autocomplete
                    v-model="formValues.permissions"
                    variant="outlined"
                    label="Permissoes do Role *"
                    :clearable="!(props.readonlyInputs.permissions || props.readonly)"
                    :menu-icon="props.readonlyInputs.permissions || props.readonly ? '' : undefined"
                    multiple
                    chips
                    closable-chips
                    :rules="formRules.permissions"
                    :messages="formMessages.permissions"
                    :error-messages="formErrors.permissions"
                    :disabled="props.disabledInputs.permissions || props.disabled || isSubmitting || fetchingAppDetails || fetchingRoleDetails"
                    :readonly="props.readonlyInputs.permissions || props.readonly"
                    :loading="fetchingAppDetails || fetchingRoleDetails || isSubmitting"
                    :items="permissionsList"
                    item-value="id"
                    item-title="display_name"
                />
            </v-col>
        </v-row>
    </v-form>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import Accesses from "@/api/Accesses";
import AccessRoles from "@/api/AccessRoles";
import Permissions from "@/api/Permissions";

const props = defineProps({
    accessId: {
        type: String,
        default: "",
    },
    access: {
        type: Object,
        default: () => ({}),
    },
    role: {
        type: Object,
        default: () => ({}),
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    mode: {
        type: String,
        default: "create",
    },
    disabledInputs: {
        type: Object,
        default: () => ({}),
    },
    readonlyInputs: {
        type: Object,
        default: () => ({}),
    },
});

const formPropertiesTemplate = {
    access_id: "",
    name: "",
    display_name: "",
    description: "",
    permissions: null,
};

const formMessagesTemplate = {
    access_id: [],
    name: [],
    display_name: [],
    description: [],
    permissions: [],
};

const valid = ref(false);
const accessRoleForm = ref(null);
const formValues = ref({ ...formPropertiesTemplate });
const formMessages = ref({ ...formMessagesTemplate });
const formErrors = ref({ ...formMessagesTemplate });
const accessInfo = ref({});
const fetchingAppDetails = ref(false);
const fetchingRoleDetails = ref(false);
const isSubmitting = ref(false);

const formRules = {
    access_id: [
        (value) => Boolean(value) || "O Acesso / Aplicacao e obrigatorio.",
    ],
    name: [
        (value) => Boolean(value) || "O codigo do Role e obrigatorio.",
    ],
    display_name: [
        (value) => Boolean(value) || "O nome do Role e obrigatorio.",
    ],
    description: [],
    permissions: [
        (value) => Array.isArray(value) && value.length > 0 || "E necessario selecionar pelo menos uma permissao.",
    ],
};

const appsList = computed(() => {
    const accessId = getResolvedAccessId();
    const accessName = props.access?.name || accessInfo.value?.name || accessId;

    if (!accessId) {
        return [];
    }

    return [
        {
            id: accessId,
            name: accessName,
        },
    ];
});

const permissionsList = computed(() => {
    const permissions = accessInfo.value?.permissions;

    if (Array.isArray(permissions)) {
        return permissions;
    }

    return [];
});

defineExpose({
    validate,
    submit,
});

onMounted(async () => {
    const resolvedAccessId = getResolvedAccessId();

    if (resolvedAccessId) {
        formValues.value.access_id = resolvedAccessId;
        await fetchAccessInformation(resolvedAccessId);
    }

    if (props.mode !== "create" && props.role?.id) {
        await fetchRoleInformation(resolvedAccessId, props.role.id);
        return;
    }

    populateForm(props.role);
});

function getResolvedAccessId() {
    return String(props.accessId || props.access?.id || formValues.value.access_id || "").trim();
}

function getResolvedRoleId() {
    return String(props.role?.id || "").trim();
}

function normalizePermissions(permissions) {
    if (!Array.isArray(permissions)) {
        return [];
    }

    return permissions
        .map((permission) => {
            if (typeof permission === "string") {
                return permission;
            }

            return permission?.id || permission?.name || null;
        })
        .filter(Boolean);
}

function normalizePermissionsPayload(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (Array.isArray(payload?.permissions)) {
        return payload.permissions;
    }

    return [];
}

function populateForm(role = {}) {
    formValues.value = {
        access_id: getResolvedAccessId(),

        name: props.mode !== "create"
            ? role?.name || ""
            : formValues.value.name,

        display_name: props.mode !== "create"
            ? role?.display_name || ""
            : formValues.value.display_name,

        description: props.mode !== "create"
            ? role?.description || ""
            : formValues.value.description,

        permissions: props.mode !== "create"
            ? normalizePermissions(role?.permissions)
            : null,
    };
}

async function fetchAccessInformation(accessId = "") {
    if (!accessId) {
        accessInfo.value = {};
        return null;
    }

    fetchingAppDetails.value = true;

    try {
        const [accessResponse, permissionsResponse] = await Promise.all([
            Accesses.get(accessId),
            Permissions.listAll(accessId),
        ]);

        const accessPayload = accessResponse?.data ?? {};
        const permissions = normalizePermissionsPayload(permissionsResponse?.data);

        accessInfo.value = {
            ...accessPayload,
            permissions,
        };

        console.log("Aplicacao carregada:", accessInfo.value);
        console.log("Permissoes carregadas:", permissions);

        return accessInfo.value;
    } catch (error) {
        console.error(
            "Erro ao carregar a aplicacao/permissoes do role:",
            error?.response?.data || error
        );

        accessInfo.value = {};
        return null;
    } finally {
        fetchingAppDetails.value = false;
    }
}

async function fetchRoleInformation(accessId = "", roleId = "") {
    if (!accessId || !roleId) {
        populateForm(props.role);
        return null;
    }

    fetchingRoleDetails.value = true;

    try {
        const response = await AccessRoles.get(accessId, roleId);
        const role = response?.data ?? props.role ?? {};
        populateForm(role);
        return role;
    } catch (error) {
        console.error("Erro ao carregar o role:", error?.response?.data || error);
        populateForm(props.role);
        return null;
    } finally {
        fetchingRoleDetails.value = false;
    }
}

async function validate() {
    const result = await accessRoleForm.value?.validate?.();
    return Boolean(result?.valid);
}

function buildPayload() {
    return {
        id: getResolvedRoleId() || undefined,
        access_id: getResolvedAccessId(),
        name: formValues.value.name?.trim() || "",
        display_name: formValues.value.display_name?.trim() || "",
        description: formValues.value.description?.trim() || "",
        permissions: normalizePermissions(formValues.value.permissions),
    };
}

async function submit() {
    isSubmitting.value = true;

    try {
        const passedValidation = await validate();

        if (!passedValidation) {
            return false;
        }

        const accessId = getResolvedAccessId();
        const roleId = getResolvedRoleId();
        const payload = buildPayload();

        if (props.mode === "edit" && roleId) {
            const response = await AccessRoles.update(accessId, payload, roleId);
            return response?.data ?? true;
        }

        const response = await AccessRoles.create(accessId, payload);
        return response?.data ?? true;
    } catch (error) {
        console.error("Erro ao guardar role:", error?.response?.data || error);
        throw error;
    } finally {
        isSubmitting.value = false;
    }
}
</script>

<style scoped>
</style>